import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import * as cornerstone from 'cornerstone-core';
import * as cornerstoneTools from 'cornerstone-tools';
import * as cornerstoneMath from 'cornerstone-math';
import * as cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';
import Hammer from 'hammerjs';
import * as dicomParser from 'dicom-parser';
import { drawFindingsForCurrentImage, clearAnnotations } from '../utils/annotationDrawer';
import './ImageViewer.css';

// Initialize Cornerstone
cornerstoneTools.external.cornerstone = cornerstone;
cornerstoneTools.external.Hammer = Hammer;
cornerstoneTools.external.cornerstoneMath = cornerstoneMath;
cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
cornerstoneWADOImageLoader.external.dicomParser = dicomParser;

const ImageViewer = forwardRef(({ studyData, patientId, layout = '1x1', onImageChange }, ref) => {
  const viewportRefs = useRef([]);
  const [viewportCount, setViewportCount] = useState(1);
  const [stacks, setStacks] = useState([]);
  const [activeViewport, setActiveViewport] = useState(0);
  const [viewportsEnabled, setViewportsEnabled] = useState(false);
  const [viewportInfo, setViewportInfo] = useState([]);
  const [thumbnails, setThumbnails] = useState({});
  const [loadedStacks, setLoadedStacks] = useState({}); // Track which stack is loaded in each viewport

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const notifyImageChange = (element, stack) => {
    if (!onImageChange || !element || !stack) {
      return;
    }

    try {
      const currentStack = stack;
      const currentIndex = currentStack.currentImageIdIndex || 0;

      onImageChange({
        imageIndex: currentIndex,
        totalImages: currentStack.imageIds?.length || stack.imageIds.length,
        stackId: currentStack.stackId || stack.stackId
      });
    } catch (error) {
      console.warn('Error notifying image change:', error);
    }
  };

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    drawAnnotations: (findingsData) => {
      if (!findingsData) {
        console.warn('Cannot draw annotations: missing data');
        return;
      }

      // Get current image info from the active viewport
      const element = viewportRefs.current[activeViewport];
      if (!element) {
        console.warn('No active viewport element');
        return;
      }

      try {
        const stackState = cornerstoneTools.getToolState(element, 'stack');
        let currentIndex = 0;
        let stackId = null;

        if (stackState && stackState.data && stackState.data.length > 0) {
          const stack = stackState.data[0];
          currentIndex = stack.currentImageIdIndex || 0;
          stackId = stack.stackId;
        }

        console.log('Drawing annotations for image:', currentIndex, 'stack:', stackId);

        // Draw the annotations
        drawFindingsForCurrentImage(viewportRefs.current, findingsData, {
          stackId,
          imageIndex: currentIndex
        });
      } catch (error) {
        console.error('Error drawing annotations:', error);
      }
    },
    clearAnnotations: () => {
      const element = viewportRefs.current[activeViewport];
      if (element) {
        clearAnnotations(element);
      }
    }
  }), [activeViewport]);

  // Calculate viewport grid from layout
  const getGridDimensions = () => {
    const [rows, cols] = layout.split('x').map(Number);
    return { rows, cols, total: rows * cols };
  };

  useEffect(() => {
    const { total } = getGridDimensions();
    setViewportCount(total);
    setViewportsEnabled(false); // Reset when layout changes
    setViewportInfo(Array(total).fill(null)); // Initialize viewport info array
  }, [layout]);

  useEffect(() => {
    if (!studyData || !patientId) return;

    // Load stacks from backend
    fetch(`${BACKEND_URL}/api/studies/${patientId}/instances`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then(stacks => {
        setStacks(stacks);
        console.log('Stacks loaded from backend:', stacks.length, 'stacks');
        stacks.forEach((stack, index) => {
          console.log(`Stack ${index + 1}/${stacks.length} (${stack.stackId}) has`, stack.imageIds.length, 'images');
          console.log(`Stack ${index + 1}/${stacks.length} (${stack.stackId}) first image ID:`, stack.imageIds[0]);
        });
        // Generate thumbnails in background after a short delay
        // This allows the main UI to render first
        setTimeout(() => {
          generateThumbnails(stacks);
        }, 100);
      })
      .catch(error => {
        console.error('Error loading stacks from backend:', error);
      });
  }, [studyData, patientId]);

  useEffect(() => {
    // Small delay to ensure DOM elements are ready
    const timer = setTimeout(() => {
      let allEnabled = true;

      // Enable all viewports
      viewportRefs.current.forEach((element, index) => {
        if (element) {
          try {
            // Check if already enabled
            cornerstone.getEnabledElement(element);
          } catch (e) {
            // Not enabled, so enable it
            try {
              cornerstone.enable(element);
              console.log(`Viewport ${index} enabled`);

              // Resize to ensure canvas matches container
              cornerstone.resize(element);

              // Add tools to each viewport element
              try {
                cornerstoneTools.addToolForElement(element, cornerstoneTools.WwwcTool);
                cornerstoneTools.addToolForElement(element, cornerstoneTools.ZoomTool, {
                  configuration: {
                    invert: false,
                    preventZoomOutsideImage: false,
                    minScale: .1,
                    maxScale: 20.0,
                  }
                });
                cornerstoneTools.addToolForElement(element, cornerstoneTools.PanTool);
                cornerstoneTools.addToolForElement(element, cornerstoneTools.StackScrollMouseWheelTool);
                cornerstoneTools.addToolForElement(element, cornerstoneTools.LengthTool);
                cornerstoneTools.addToolForElement(element, cornerstoneTools.EllipticalRoiTool);
                cornerstoneTools.addToolForElement(element, cornerstoneTools.RectangleRoiTool);
                cornerstoneTools.addToolForElement(element, cornerstoneTools.AngleTool);
                cornerstoneTools.addToolForElement(element, cornerstoneTools.MagnifyTool);
                cornerstoneTools.addToolForElement(element, cornerstoneTools.FreehandRoiTool);
                cornerstoneTools.addToolForElement(element, cornerstoneTools.ProbeTool);

                // Activate default tools for this element
                cornerstoneTools.setToolActiveForElement(element, 'Wwwc', { mouseButtonMask: 1 });
                cornerstoneTools.setToolActiveForElement(element, 'Pan', { mouseButtonMask: 4 });
                cornerstoneTools.setToolActiveForElement(element, 'Zoom', { mouseButtonMask: 2 });
                cornerstoneTools.setToolActiveForElement(element, 'StackScrollMouseWheel', {});

                // Set annotation tools to passive mode so they render but don't interact
                cornerstoneTools.setToolPassiveForElement(element, 'FreehandRoi');
                cornerstoneTools.setToolPassiveForElement(element, 'Probe');
              } catch (toolError) {
                console.warn('Error setting up tools for viewport:', toolError.message);
              }

              // Handle viewport click for activation
              const mousedownHandler = () => {
                setActiveViewport(index);
              };
              element.addEventListener('mousedown', mousedownHandler);
              element._mousedownHandler = mousedownHandler;

              // Prevent default context menu on right-click so mouse-2 zoom works without browser menu
              const contextmenuHandler = (ev) => {
                try { ev.preventDefault(); } catch (e) { }
              };
              element.addEventListener('contextmenu', contextmenuHandler);
              element._contextmenuHandler = contextmenuHandler;
            } catch (enableError) {
              console.error('Error enabling viewport:', enableError);
              allEnabled = false;
            }
          }
        } else {
          allEnabled = false;
        }
      });

      if (allEnabled && viewportRefs.current.length > 0) {
        console.log('All viewports enabled');
        setViewportsEnabled(true);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      viewportRefs.current.forEach(element => {
        if (element) {
          try {
            // Remove attached DOM event handlers if present
            try {
              if (element._mousedownHandler) {
                element.removeEventListener('mousedown', element._mousedownHandler);
                delete element._mousedownHandler;
              }
              if (element._contextmenuHandler) {
                element.removeEventListener('contextmenu', element._contextmenuHandler);
                delete element._contextmenuHandler;
              }
              if (element._renderedHandler) {
                element.removeEventListener('cornerstoneimagerendered', element._renderedHandler);
                delete element._renderedHandler;
              }
              if (element._newImageHandler) {
                element.removeEventListener('cornerstonenewimage', element._newImageHandler);
                delete element._newImageHandler;
              }
            } catch (inner) { }

            cornerstone.getEnabledElement(element);
            cornerstone.disable(element);
          } catch (e) {
            // Element not enabled, nothing to disable
          }
        }
      });
    };
  }, [viewportCount]);

  const updateViewportInfo = (stack, viewportIndex) => {
    const element = viewportRefs.current[viewportIndex];
    if (!element) return;

    try {
      const enabledElement = cornerstone.getEnabledElement(element);
      const viewport = enabledElement.viewport;
      const image = enabledElement.image;

      // Get stack state for image number
      let imageNumber = '1/1';
      try {
        const currentIndex = stack.currentImageIdIndex || 0;
        imageNumber = `${currentIndex + 1}/${stack.imageIds.length}`;
      } catch (e) {
        // No stack state available
      }

      // Calculate FPS from render time
      const renderTime = enabledElement.renderTimeInMs || 0;
      const fps = renderTime > 0 ? Math.round(1000 / renderTime) : 0;

      // Get zoom percentage
      const zoom = Math.round((viewport.scale || 1) * 100);

      // Get window width and center
      const ww = Math.round(viewport.voi.windowWidth || 0);
      const wc = Math.round(viewport.voi.windowCenter || 0);

      // Update viewport info
      setViewportInfo(prev => {
        const newInfo = [...prev];
        newInfo[viewportIndex] = {
          fps,
          renderTime: `${renderTime.toFixed(0)}ms`,
          imageNumber,
          zoom: `${zoom}%`,
          ww,
          wc
        };
        return newInfo;
      });
    } catch (e) {
      console.warn('Error updating viewport info:', e);
    }
  };

  const generateThumbnails = async (stacks) => {
    const batchSize = 6; // Process 6 thumbnails at a time

    const generateThumbnail = async (stack) => {
      if (!stack.imageIds || stack.imageIds.length === 0) return null;

      try {
        const imageId = stack.imageIds[0];
        const image = await cornerstone.loadAndCacheImage(imageId);

        // Create temporary enabled element for rendering
        const tempElement = document.createElement('div');
        const thumbnailSize = 100;
        tempElement.style.width = `${thumbnailSize}px`;
        tempElement.style.height = `${thumbnailSize}px`;
        tempElement.style.position = 'absolute';
        tempElement.style.left = '-9999px';
        tempElement.style.visibility = 'hidden';
        document.body.appendChild(tempElement);

        try {
          cornerstone.enable(tempElement);
          await cornerstone.displayImage(tempElement, image);

          // Apply same viewport transformations as main viewport
          cornerstone.reset(tempElement);
          cornerstone.fitToWindow(tempElement);

          // Wait for rendering to complete
          await new Promise(resolve => requestAnimationFrame(resolve));

          const enabledElement = cornerstone.getEnabledElement(tempElement);
          const thumbnailUrl = enabledElement?.canvas?.toDataURL();

          cornerstone.disable(tempElement);
          document.body.removeChild(tempElement);

          return { stackId: stack.stackId, thumbnailUrl };
        } catch (error) {
          document.body.removeChild(tempElement);
          throw error;
        }
      } catch (error) {
        console.warn(`Failed to generate thumbnail for stack ${stack.stackId}:`, error);
        return null;
      }
    };

    // Process in batches to avoid blocking UI
    for (let i = 0; i < stacks.length; i += batchSize) {
      const batch = stacks.slice(i, i + batchSize);
      const results = await Promise.all(batch.map(generateThumbnail));

      // Update state with this batch
      setThumbnails(prev => {
        const updated = { ...prev };
        results.forEach(result => {
          if (result && result.thumbnailUrl) {
            updated[result.stackId] = result.thumbnailUrl;
          }
        });
        return updated;
      });

      // Small delay between batches to keep UI responsive
      if (i + batchSize < stacks.length) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
  };

  const loadStackIntoViewport = (viewportIndex, stack) => {
    const element = viewportRefs.current[viewportIndex];
    if (!element || !stack || stack.imageIds.length === 0) {
      console.warn('Cannot load stack: missing element or stack data');
      return;
    }

    // Verify element is enabled before loading
    try {
      cornerstone.getEnabledElement(element);
    } catch (e) {
      console.error('Cannot load stack: element not enabled', e);
      return;
    }

    console.log('Loading image:', stack.imageIds[0]);

    cornerstone.loadAndCacheImage(stack.imageIds[0]).then(image => {
      console.log('Image loaded successfully, displaying...');
      console.log('Image dimensions:', image.width, 'x', image.height);
      console.log('Element dimensions:', element.clientWidth, 'x', element.clientHeight);

      // Ensure canvas is sized correctly before displaying
      cornerstone.resize(element);

      cornerstone.displayImage(element, image);

      // Reset and fit viewport
      cornerstone.reset(element);
      cornerstone.fitToWindow(element);

      const viewport = cornerstone.getViewport(element);
      console.log('Viewport after display:', viewport);

      if (stack.imageIds.length > 1) {
        cornerstoneTools.addStackStateManager(element, ['stack']);
        cornerstoneTools.addToolState(element, 'stack', stack);
      }

      // Track which stack is loaded in this viewport
      setLoadedStacks(prev => ({
        ...prev,
        [viewportIndex]: stack.stackId
      }));

      // Remove any previous image listeners attached to this element
      try {
        if (element._renderedHandler) {
          element.removeEventListener('cornerstoneimagerendered', element._renderedHandler);
        }
        if (element._newImageHandler) {
          element.removeEventListener('cornerstonenewimage', element._newImageHandler);
        }
      } catch (e) { }

      // Add event listeners for viewport updates and store handlers for cleanup
      const renderedHandler = () => updateViewportInfo(stack, viewportIndex);
      const newImageHandler = () => {
        updateViewportInfo(stack, viewportIndex);
        notifyImageChange(element, stack);
      };
      element.addEventListener('cornerstoneimagerendered', renderedHandler);
      element.addEventListener('cornerstonenewimage', newImageHandler);
      element._renderedHandler = renderedHandler;
      element._newImageHandler = newImageHandler;

      // Notify parent about initial image load
      notifyImageChange(element, stack);

      // Initial update
      updateViewportInfo(stack, viewportIndex);

    }).catch(error => {
      console.error('Error loading image:', error);
      console.error('Image ID was:', stack.imageIds[0]);
    });
  };

  const handleDrop = (e, viewportIndex) => {
    e.preventDefault();
    const stackIndex = parseInt(e.dataTransfer.getData('stackIndex'));
    if (!isNaN(stackIndex) && stacks[stackIndex]) {
      loadStackIntoViewport(viewportIndex, stacks[stackIndex]);
    }
  };

  const { rows, cols } = getGridDimensions();
  const viewportWidth = `${100 / cols}%`;
  const viewportHeight = `${100 / rows}%`;

  // Black placeholder image data URL
  const placeholderImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iIzAwMDAwMCIvPjwvc3ZnPg==';

  return (
    <div className="imageViewer">
      {/* Thumbnail Panel */}
      {stacks.length > 0 && (
        <div className="thumbnailSelector">
          {stacks.map((stack, index) => (
            <div
              key={index}
              className={`thumbnail ${loadedStacks[activeViewport] === stack.stackId ? 'selected' : ''}`}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('stackIndex', index);
              }}
              onClick={() => {
                loadStackIntoViewport(activeViewport, stack);
              }}
            >
              <img
                src={thumbnails[stack.stackId] || placeholderImage}
                alt={`Series ${stack.stackId}`}
                className="thumbnail-image"
              />
              <div className="thumbnail-info">
                <div>{stack.modality || 'Unknown'}</div>
                <div>{stack.imageIds.length} images</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Viewports Container */}
      <div className="viewportsContainer">
        {Array.from({ length: viewportCount }).map((_, index) => (
          <div
            key={index}
            className={`viewportWrapper ${activeViewport === index ? 'active' : ''}`}
            style={{
              width: viewportWidth,
              height: viewportHeight
            }}
          >
            <div
              ref={el => viewportRefs.current[index] = el}
              className="viewport"
              data-index={index}
              onDrop={(e) => handleDrop(e, index)}
              onDragOver={(e) => e.preventDefault()}
            />

            {/* Viewport Overlay */}
            <div className="overlay">
              <div className="top-left overlay-element">
                <div>Image: {viewportInfo[index]?.imageNumber || '1/1'}</div>
              </div>
              <div className="bottom-left overlay-element">
                <div>Zoom: {viewportInfo[index]?.zoom || '100%'}</div>
                <div>WW/WC: {viewportInfo[index]?.ww || 0}/{viewportInfo[index]?.wc || 0}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

export default ImageViewer;
